/**
 * 기사편집 v2 — (기사타입 × 기사권한) 조합표.
 *
 * ── v1 이 왜 깨졌나 ────────────────────────────────────────────────────────
 * v1 `showAndHide()` 는 클래스 목록 두 개를 만들어 순서대로 돌린다.
 *
 *     showList.forEach(cls => $(`.${cls}`).show());
 *     hideList.forEach(cls => $(`.${cls}`).hide());
 *
 * `vo`(소유자)/`vs`(소속자) 축과 `xs`/`xb`/`xc`(타입) 축이 서로를 모른 채 각각 show/hide 를
 * 부르므로, `class="vo xs"` 인 요소의 최종 상태가 **어느 쪽이 나중에 실행됐는지**에 달렸다.
 * hide 가 뒤에 오는 지금은 우연히 맞지만 순서가 바뀌면 깨진다. 게다가 실제 마크업에
 * `xb`·`xc` 는 **0개**여서 발주자와 업체발주자 화면이 완전히 같았고, `me`·`ve` 는 CSS 에도
 * hideList 에도 없어 항상 보였다.
 *
 * ── v2 ────────────────────────────────────────────────────────────────────
 * 화면 배치는 v1 로 되돌렸지만(탭 없음, 4단 그대로) **가시성 판정 방식은 되돌리지 않았다.**
 * 조합에 해당하지 않는 블록은 CSS 로 숨기는 게 아니라 **서버가 렌더하지 않는다**
 * (edit-v2.blade.php 의 `$showCar` / `$showSettle` / `$showCompany`).
 * 그래야 "화면에 없으면 POST 에도 없다" 가 성립하고 v1 의 팬텀 제출 사고가 재발하지 않는다.
 *
 * 그래서 이 파일이 하는 일은 두 가지로 좁아졌다.
 *   1) 조합별로 어떤 블록이 있어야 하는지의 **정본 표** (서버 렌더 조건과 짝을 맞춘 문서)
 *   2) 폼에서 타입·권한을 바꿨을 때 지금 화면 구성이 새 조합과 어긋나는지 **판정**
 *      → 어긋나면 "저장하면 화면이 새로 구성됩니다" 로 알린다 (마크업을 즉석에서 못 만든다)
 *
 * 설계: .ai/domains/member-edit-v2.md §8
 */

const TYPES   = ['수발주자', '발주자', '업체발주자', ''];   // '' = 타입 미설정
const PERMITS = ['소유자', '소속자'];

/**
 * 조합별로 렌더돼야 하는 블록.
 *   car     — 기사/장비 · 매칭반경 · 차고지 · GPS · 수주승인 · 발주개수 · 차수 · -6차
 *   settle  — 회비결제방식 · 계좌 3종 · 보유캐시   (소유자 전유물, 기존회원만)
 *   company — 사업자정보                          (g5_company, 소유자 귀속)
 *
 * 운영 분포(mem_status=0): 수발주자 소유자 9,604 / 소속자 1,069 · 발주자 3,316 / 6 ·
 * 업체발주자 1,748 / 1 · 미설정 3 / 0 — 8조합 중 7개가 실존한다.
 */
const BLOCKS = {
    '수발주자|소유자'  : ['car', 'settle', 'company'],
    '수발주자|소속자'  : ['car'],
    '발주자|소유자'    : ['settle', 'company'],       // 장비가 없다
    '발주자|소속자'    : [],
    '업체발주자|소유자': ['settle', 'company'],
    '업체발주자|소속자': [],
    '|소유자'          : ['settle', 'company'],       // 타입 미설정 — 최소만
    '|소속자'          : [],
};

/** 사업자정보 안내문 — 업체발주자는 사실상 필수다 */
const COMPANY_HINT = {
    '업체발주자': '업체발주자는 필수',
    '발주자'    : '선택 항목',
    '수발주자'  : '',
    ''          : '',
};

export default class Visibility {
    static key(driverType, permit) {
        const t = TYPES.includes(driverType) ? driverType : '';
        const p = PERMITS.includes(permit) ? permit : '소유자';
        return `${t}|${p}`;
    }

    /** 조합에 해당하는 블록 목록. 표에 없는 조합이면 빈 배열(기본정보·상담만). */
    static blocks(driverType, permit) {
        return BLOCKS[this.key(driverType, permit)] || [];
    }

    /**
     * 지금 DOM 의 블록 구성이 주어진 조합과 맞는지.
     * `settle` 은 "소유자 + 기존회원" 조건이라 신규등록에서 없는 게 정상이므로 판정에서 뺀다.
     */
    static matchesDom(driverType, permit) {
        const want = this.blocks(driverType, permit).filter((b) => b !== 'settle').sort();
        const has  = ['car', 'company'].filter((b) => $(`[data-me-block="${b}"]`).length > 0).sort();

        return want.join(',') === has.join(',');
    }

    /** 타입에 따른 사업자정보 안내문 — 이건 즉시 반영 가능하다 */
    static applyHint(driverType) {
        $('#meCompanyHint').text(COMPANY_HINT[driverType] ?? '');
    }
}
